import { useMemo } from 'react';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { formatLongDate } from '~/utils/dateFormat';

export type PostKind = components['schemas']['PostKind'];
export type Language = components['schemas']['Language'];
type PostSummary = components['schemas']['PostSummary'];
type PostDetail = components['schemas']['PostDetail'];

/** How many posts to request per page for the infinite-scroll feeds. */
const PER_PAGE = 12;

export type PostFormat = 'news' | 'tutorials';

/**
 * The v3 posts collection is one flat set filtered by `kind`. Which kind a
 * screen shows depends on who's viewing: a study helper sees the study-helper
 * stream, everyone else sees the student stream.
 */
export function kindForRole(
  isStudyHelper: boolean,
  format: PostFormat,
): PostKind {
  if (format === 'news') {
    return isStudyHelper ? 'study_helper_news' : 'student_news';
  }
  return isStudyHelper ? 'study_helper_tutorials' : 'student_tutorials';
}

export interface ArticleListItem {
  slug: string;
  title: string;
  date: string;
  description: string;
  imageUrl?: string;
}

/** Adapts a post to the card shape the shared article components consume. */
export function toListItem(post: PostSummary): ArticleListItem {
  return {
    slug: post.slug,
    title: post.title,
    date: formatLongDate(post.published_at),
    description: post.excerpt,
    imageUrl: post.featured_image_url ?? post.thumbnail_url ?? undefined,
  };
}

export interface ArticleDetail {
  title: string;
  content: string;
  imageUrl?: string;
  date?: string;
}

/** Adapts a post to the shape `ArticleDetailScreen` renders. */
export function toArticleDetail(post: PostDetail): ArticleDetail {
  return {
    title: post.title,
    content: post.content,
    imageUrl: post.featured_image_url ?? undefined,
    date: formatLongDate(post.published_at),
  };
}

/**
 * Infinite-scroll feed of one post kind. This exists because the offset
 * paging it sets up is non-trivial and three screens need it identically —
 * single-shot reads call `api.getPost.useQuery` directly instead.
 *
 * `enabled` exists so a caller whose `kind` depends on the viewer's role can
 * hold the request until the role is known, instead of fetching the student
 * stream and then re-fetching the study-helper one. `language` is required by
 * the endpoint: WordPress translations share a slug, so a feed is only
 * well-defined for one language.
 */
export function usePostsFeed(
  kind: PostKind,
  language: Language,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;

  const query = api.listPosts.useInfiniteQuery(
    { query: { kind, language, limit: PER_PAGE } },
    {
      enabled,
      initialPageParam: { query: { offset: 0 } },
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const nextOffset = (lastPageParam.query?.offset ?? 0) + PER_PAGE;
        return nextOffset < lastPage.count
          ? { query: { offset: nextOffset } }
          : undefined;
      },
    },
  );

  const items = useMemo(
    () =>
      (query.data?.pages ?? []).flatMap((page) => page.data).map(toListItem),
    [query.data],
  );

  return {
    items,
    // A disabled query is idle, so TanStack reports `isLoading: false` — but the
    // screen is still waiting on data, so surface it as loading and keep the
    // empty state from flashing.
    isLoading: !enabled || query.isLoading || query.isFetchingNextPage,
    hasMore: Boolean(query.hasNextPage),
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        void query.fetchNextPage();
      }
    },
  };
}
